import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

type Units = 'metric' | 'imperial';

interface UnitsContextType {
  units: Units;
  toggleUnits: () => void;
  kg: (val: number) => string;
  cm: (val: number) => string;
  displayWeight: (val: number) => string;
  displayHeight: (val: number) => string;
}

export const UnitsContext = createContext<UnitsContextType | null>(null);

export function UnitsProvider({ children }: { children: React.ReactNode }) {
  const [units, setUnits] = useState<Units>('metric');

  useEffect(() => {
    const load = async () => {
      try {
        const saved = localStorage.getItem('helios_units');
        if (saved === 'metric' || saved === 'imperial') {
          setUnits(saved as Units);
        }
      } catch {
        // ignore localStorage errors
      }
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from('profiles')
        .select('units')
        .eq('id', user.id)
        .single();
      if (data?.units) setUnits(data.units as Units);
    };
    load();
  }, []);

  const toggleUnits = async () => {
    const newUnits = units === 'metric' ? 'imperial' : 'metric';
    setUnits(newUnits);
    try {
      localStorage.setItem('helios_units', newUnits);
    } catch {
      // ignore localStorage errors
    }
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase.from('profiles').update({ units: newUnits }).eq('id', user.id);
    }
  };

  // Conversion helpers: input is always metric (kg, cm); output is formatted for display
  const displayWeight = (kgVal: number) => units === 'imperial'
    ? `${Math.round(kgVal * 2.205)}lb`
    : `${kgVal}kg`;

  const displayHeight = (cmVal: number) => {
    if (units === 'imperial') {
      const totalInches = cmVal / 2.54;
      const feet = Math.floor(totalInches / 12);
      const inches = Math.round(totalInches % 12);
      return `${feet}'${inches}"`;
    }
    return `${cmVal}cm`;
  };

  const kg = displayWeight;
  const cm = displayHeight;

  return (
    <UnitsContext.Provider value={{ units, toggleUnits, kg, cm, displayWeight, displayHeight }}>
      {children}
    </UnitsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useUnits = () => {
  const ctx = useContext(UnitsContext);
  if (!ctx) throw new Error('useUnits must be used within UnitsProvider');
  return ctx;
};
